/* eslint-disable react/no-unknown-property */
import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const Laptop = ({ position = [0, 0.79, -1.9], isTyping = false, screenGlow = false }) => {
  const screenRef = useRef();
  const glowIntensity = useRef(0);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // Screen glow animation when AI is generating
    if (screenGlow) {
      glowIntensity.current = THREE.MathUtils.lerp(glowIntensity.current, 0.8 + Math.sin(time * 4) * 0.2, 0.1);
    } else {
      glowIntensity.current = THREE.MathUtils.lerp(glowIntensity.current, 0.3, 0.1);
    }
    
    if (screenRef.current) {
      screenRef.current.material.emissiveIntensity = glowIntensity.current;
    }
  });

  return (
    <group position={position} rotation={[0, Math.PI, 0]}>
      {/* Laptop Base */}
      <mesh castShadow receiveShadow>
        <boxGeometry args={[0.3, 0.015, 0.2]} />
        <meshStandardMaterial color="#C0C0C0" roughness={0.3} metalness={0.7} />
      </mesh>
      
      {/* Keyboard area */}
      <mesh position={[0, 0.008, 0.02]}>
        <boxGeometry args={[0.26, 0.002, 0.14]} />
        <meshStandardMaterial color="#1C1917" roughness={0.8} />
      </mesh>
      
      {/* Keyboard keys (simplified) */}
      {[...Array(4)].map((_, row) => (
        [...Array(10)].map((_, col) => (
          <mesh 
            key={`key-${row}-${col}`}
            position={[
              -0.11 + col * 0.024,
              0.01,
              -0.04 + row * 0.03
            ]}
          >
            <boxGeometry args={[0.02, 0.003, 0.025]} />
            <meshStandardMaterial color="#2D2D2D" roughness={0.7} />
          </mesh>
        ))
      ))}
      
      {/* Trackpad */}
      <mesh position={[0, 0.009, 0.07]}>
        <boxGeometry args={[0.08, 0.001, 0.05]} />
        <meshStandardMaterial color="#3D3D3D" roughness={0.5} />
      </mesh>
      
      {/* Screen (hinged part) */}
      <group position={[0, 0.007, -0.1]} rotation={[-0.3, 0, 0]}>
        {/* Screen back */}
        <mesh castShadow>
          <boxGeometry args={[0.3, 0.2, 0.01]} />
          <meshStandardMaterial color="#C0C0C0" roughness={0.3} metalness={0.7} />
        </mesh>
        
        {/* Screen display */}
        <mesh ref={screenRef} position={[0, 0, 0.006]}>
          <planeGeometry args={[0.27, 0.17]} />
          <meshStandardMaterial 
            color={screenGlow ? "#1a1a2e" : "#0a0a0f"}
            emissive="#BA7B7C"
            emissiveIntensity={0.3}
            roughness={0.1}
          />
        </mesh>
        
        {/* Screen content glow lines (when active) */}
        {screenGlow && (
          <>
            {[...Array(5)].map((_, i) => (
              <mesh key={`line-${i}`} position={[-0.08, 0.05 - i * 0.025, 0.007]}>
                <planeGeometry args={[0.15 - i * 0.02, 0.008]} />
                <meshBasicMaterial 
                  color="#FFB6C1" 
                  transparent 
                  opacity={0.6 - i * 0.1} 
                />
              </mesh>
            ))}
            {/* Typing cursor */}
            <mesh position={[0.05, -0.03, 0.007]}>
              <planeGeometry args={[0.005, 0.015]} />
              <meshBasicMaterial color="#FFFFFF" transparent opacity={0.8} />
            </mesh>
          </>
        )}
        
        {/* Apple-like logo */}
        <mesh position={[0, 0, -0.006]}>
          <circleGeometry args={[0.015, 16]} />
          <meshStandardMaterial color="#A0A0A0" roughness={0.4} metalness={0.6} />
        </mesh>
      </group>
      
      {/* Hinge */}
      <mesh position={[0, 0.007, -0.095]}>
        <cylinderGeometry args={[0.008, 0.008, 0.28, 8]} rotation={[0, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#808080" roughness={0.4} metalness={0.5} />
      </mesh>
    </group>
  );
};

export default Laptop;
